import React, { useEffect } from "react"
import Paper from "../../ui/Paper"
import { useDispatch, useSelector } from "react-redux"
import { selectUpdatingUserRSO, updateUserRSOTLE } from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import FormError from "../../ui/FormError"
import TextArea from "../../ui/TextArea"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import Tip from "../../ui/Tip"
import { validateTLE } from "./tle"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 50vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

/**
 * The form validator
 */
const schema = yup
  .object({
    tle: yup
      .string()
      .test("is-valid-tle", "TLE is not well-formed", (value) =>
        validateTLE(value)
      )
      .required("TLE is required"),
  })
  .required()

/**
 * Create a new RSO.  User RSOs are treated differently to RSO's generated from external sources such as Spacktrack.
 *
 * @param id RSO id
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const UpdateTLE = ({
  id,
  tleLine0,
  tleLine1,
  tleLine2,
  eTag,
  handleCancel,
}) => {
  const dispatch = useDispatch()

  const inProgress = useSelector((state) => selectUpdatingUserRSO(state))

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tle: "",
    },
    resolver: yupResolver(schema),
  })

  const onSubmit = (data) => {
    data.id = id
    data._etag = eTag
    dispatch(updateUserRSOTLE(data))
      .unwrap()
      .then(() => {
        handleCancel()
      })
  }

  // Prepopulate the form with the tle
  useEffect(() => {
    if (tleLine1 && tleLine2) {
      if (tleLine0) {
        setValue("tle", `${tleLine0}\n${tleLine1}\n${tleLine2}`)
      } else {
        setValue("tle", `${tleLine1}\n${tleLine2}`)
      }
    }
  }, [id, tleLine0, tleLine1, tleLine2, setValue])

  return (
    <Panel>
      <h2>Update TLE</h2>
      <p>
        <strong>Note:</strong> this will trigger a regeneration of passes for
        the new TLE, however will <strong>not</strong> automatically update
        currently scheduled objects. Redeploy thew schedule to achieve this.
      </p>

      {inProgress?._saveStatus === "updating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {inProgress?._saveStatus === "rejected" && (
        <ErrorMsg
          msg={inProgress._errorMessage || "an error occurred updating the RSO"}
          showIcon={true}
        />
      )}
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="tle">TLE:</Label>
            <TextArea {...register("tle")} rows="3" />
            <Tip>TLE (each line must be on a new line)</Tip>
            <FormError>{errors.tle?.message}</FormError>
          </FormGroup>
          <Button type="submit" active>
            Submit
          </Button>
          {handleCancel && <Button onClick={handleCancel}>Cancel</Button>}
        </form>
      </FormContainer>
    </Panel>
  )
}

UpdateTLE.propTypes = {
  id: PropTypes.string, // RSO ID
  eTag: PropTypes.string, // RSO etag used for optimistic concurrency control (use the latest value from the RSO)
  tleLine0: PropTypes.string,
  tleLine1: PropTypes.string,
  tleLine2: PropTypes.string,
  handleCancel: PropTypes.func, // Cancel the update of an RSO
}
export default UpdateTLE
